import {
  useMailOrderStore,
  type AllergyProfile,
  type SeverityProfile,
  type ConditionProfile,
} from '../../../../../stores/useMailOrderStore';

// ─── Checkbox group helpers ───────────────────────────────────────────────────

interface CheckboxItem {
  key: string;
  label: string;
}

const ALLERGY_ITEMS: CheckboxItem[] = [
  { key: 'codeine', label: 'Codeine' },
  { key: 'sulfa', label: 'Sulfa' },
  { key: 'aspirin', label: 'Aspirin' },
  { key: 'penicillin', label: 'Penicillin' },
];

const SEVERITY_ITEMS: CheckboxItem[] = [
  { key: 'mild', label: 'Mild' },
  { key: 'moderate', label: 'Moderate' },
  { key: 'severe', label: 'Severe' },
  { key: 'intolerance', label: 'Intolerance' },
  { key: 'anaphylaxis', label: 'Anaphylaxis' },
];

const CONDITION_ITEMS: CheckboxItem[] = [
  { key: 'thyroid', label: 'Thyroid' },
  { key: 'hbp', label: 'High Blood Pressure' },
  { key: 'diabetes', label: 'Diabetes' },
  { key: 'glaucoma', label: 'Glaucoma' },
  { key: 'heart', label: 'Heart Disease' },
  { key: 'intestinal', label: 'Intestinal Disorder' },
  { key: 'lung', label: 'Lung Disease' },
];

// ─── Generic checkbox group section ──────────────────────────────────────────

interface CheckboxGroupProps {
  title: string;
  items: CheckboxItem[];
  values: Record<string, boolean | string>;
  noneChecked: boolean;
  onNone: () => void;
  onToggle: (key: string, value: boolean) => void;
  otherKey?: string;
  otherValue?: string;
  onOther?: (value: string) => void;
}

function CheckboxGroup({
  title,
  items,
  values,
  noneChecked,
  onNone,
  onToggle,
  otherKey,
  otherValue,
  onOther,
}: CheckboxGroupProps) {
  return (
    <div>
      <p className="fw-bold mb-2">{title}</p>
      <div className="form-check">
        <input
          id={`${title}-none`}
          type="checkbox"
          className="form-check-input"
          checked={noneChecked}
          onChange={e => {
            if (e.target.checked) onNone();
          }}
          style={{ marginRight: '5px' }}
        />
        <label htmlFor={`${title}-none`} className="form-check-label">
          None
        </label>
      </div>
      {items.map(({ key, label }) => (
        <div key={key} className="form-check">
          <input
            id={`${title}-${key}`}
            type="checkbox"
            className="form-check-input"
            checked={values[key] === true}
            onChange={e => onToggle(key, e.target.checked)}
            style={{ marginRight: '5px' }}
          /> 
          <label htmlFor={`${title}-${key}`} className="form-check-label">
            {label}
          </label>
        </div>
      ))}
      {otherKey && onOther && (
        <div className="mt-2">
          <p className="mb-1 small text-muted">Other</p>
          <textarea
            id={`${title}-other`}
            className="form-control form-control-sm"
            rows={3}
            value={otherValue ?? ''}
            onChange={e => onOther(e.target.value)}
            placeholder="Describe other..."
          />
        </div>
      )}
    </div>
  );
}

// ─── Step component ───────────────────────────────────────────────────────────

export function WizardStep2HealthProfile() {
  const {
    allergies,
    severity,
    conditions,
    updateAllergy,
    setAllergyNone,
    updateSeverity,
    setSeverityNone,
    updateCondition,
    setConditionNone,
  } = useMailOrderStore();

  return (
    <div>
      <h4 className="mb-1">Health Profile</h4>
      <p className="text-muted mb-4">
        Please select any known allergies, reaction severity, and chronic conditions.
      </p>

      <div className="row">
        <div className="col-12 col-md-4 mb-4 mb-md-0">
          <CheckboxGroup
            title="Allergies"
            items={ALLERGY_ITEMS}
            values={allergies as unknown as Record<string, boolean | string>}
            noneChecked={allergies.none}
            onNone={setAllergyNone}
            onToggle={(key, value) =>
              updateAllergy(key as keyof Omit<AllergyProfile, 'none' | 'other'>, value)
            }
            otherKey="other"
            otherValue={allergies.other}
            onOther={val => updateAllergy('other', val)}
          />
        </div>

        <div className="col-12 col-md-4 mb-4 mb-md-0">
          <CheckboxGroup
            title="Severity of Allergies"
            items={SEVERITY_ITEMS}
            values={severity as unknown as Record<string, boolean | string>}
            noneChecked={severity.none}
            onNone={setSeverityNone}
            onToggle={(key, value) =>
              updateSeverity(key as keyof Omit<SeverityProfile, 'none'>, value)
            }
          />
        </div>

        <div className="col-12 col-md-4">
          <CheckboxGroup
            title="Chronic Conditions"
            items={CONDITION_ITEMS}
            values={conditions as unknown as Record<string, boolean | string>}
            noneChecked={conditions.none}
            onNone={setConditionNone}
            onToggle={(key, value) =>
              updateCondition(key as keyof Omit<ConditionProfile, 'none' | 'other'>, value)
            }
            otherKey="other"
            otherValue={conditions.other}
            onOther={val => updateCondition('other', val)}
          />
        </div>
      </div>
    </div>
  );
}
